import { DEVEX_REVIEW_LATENCY_MAX_DAYS } from '@dev-console/devex-metrics';
import type { DevexReviewLatencyGroupBy, DevexReviewLatencyPoint } from '@dev-console/types';

import { GROUP_VALUE_LABELS, GROUP_VALUE_ORDER } from '@/components/devex-metrics/review-latency-labels';

export interface TrendSummary {
  current: number | null;
  delta: number | null;
}

export interface TrendWindows {
  comparisonWindowDays: number;
  groupSmoothingWindowDays: number;
  orgSmoothingWindowDays: number;
  queryDays: number;
}

// A comparison series point is keyed by its groupKey (size bucket, surface, etc.); the legacy team
// scope still arrives as teamSlug. Empty string means "ungrouped" and is filtered out by callers.
export function getGroupKey(point: DevexReviewLatencyPoint): string {
  return point.groupKey ?? point.teamSlug ?? '';
}

export function formatGroupValue(groupBy: DevexReviewLatencyGroupBy, value: string): string {
  return GROUP_VALUE_LABELS[groupBy][value] ?? value;
}

export function sortGroupKeys(groupKeys: string[], groupBy: DevexReviewLatencyGroupBy): string[] {
  const orderIndex = new Map(GROUP_VALUE_ORDER[groupBy].map((value, index) => [value, index]));
  return [...groupKeys].sort((left, right) => {
    const leftOrder = orderIndex.get(left);
    const rightOrder = orderIndex.get(right);
    if (leftOrder != null && rightOrder != null) return leftOrder - rightOrder;
    if (leftOrder != null) return -1;
    if (rightOrder != null) return 1;
    return left.localeCompare(right);
  });
}

function average(values: number[]): number | null {
  return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

export const isValidNumber = (value: number | null | undefined): value is number => value != null && !isNaN(value);

export function getTrailingAverage<T>(
  data: T[],
  endIndexInclusive: number,
  windowSize: number,
  selector: (item: T) => number | null | undefined
): number | null {
  const startIndex = Math.max(0, endIndexInclusive - windowSize + 1);
  const values = data
    .slice(startIndex, endIndexInclusive + 1)
    .map(selector)
    .filter(isValidNumber);

  return average(values);
}

// Summarizes the current and previous calendar windows for a metric.
// Latency metrics (p50, p90, etc.) should use the default 'active-days' denominator —
// missing days mean no reviews occurred, not instant reviews. Rate metrics like PRs/day
// should use 'calendar-days' so absent days count as zero in the denominator.
export function summarizeTrailingWindow<T extends { date: string }>(
  data: T[],
  windowDays: number,
  selector: (item: T) => number | null | undefined,
  options?: { denominator?: 'active-days' | 'calendar-days' }
): TrendSummary {
  if (data.length === 0) return { current: null, delta: null };

  const DAY_MS = 86_400_000; // 24 hours
  const endMs = new Date(data[data.length - 1].date + 'T00:00:00Z').getTime();
  const currentCutoffMs = endMs - windowDays * DAY_MS;
  const previousCutoffMs = currentCutoffMs - windowDays * DAY_MS;
  const toMs = (point: T) => new Date(point.date + 'T00:00:00Z').getTime();

  const aggregate = (values: number[]): number | null => {
    if (values.length === 0) return null;
    const sum = values.reduce((acc, v) => acc + v, 0);
    return options?.denominator === 'calendar-days' ? sum / windowDays : sum / values.length;
  };

  const currentValues = data
    .filter((p) => {
      const ms = toMs(p);
      return ms > currentCutoffMs && ms <= endMs;
    })
    .map(selector)
    .filter(isValidNumber);
  const previousValues = data
    .filter((p) => {
      const ms = toMs(p);
      return ms > previousCutoffMs && ms <= currentCutoffMs;
    })
    .map(selector)
    .filter(isValidNumber);

  const current = aggregate(currentValues);
  const previous = aggregate(previousValues);
  return { current, delta: current != null && previous != null ? current - previous : null };
}

export function filterTrailingDays<T extends { date: string }>(data: T[], days: number): T[] {
  if (data.length === 0) return data;
  const DAY_MS = 86_400_000; // 24 hours
  const endMs = new Date(data[data.length - 1].date + 'T00:00:00Z').getTime();
  const cutoffMs = endMs - days * DAY_MS;
  return data.filter((p) => new Date(p.date + 'T00:00:00Z').getTime() > cutoffMs);
}

// Returns smoothing and comparison window sizes for the selected time range, plus queryDays —
// the total days to fetch so that smoothing windows at the start of the range have enough data.
export function getTrendWindows(days: number): TrendWindows {
  const orgSmoothingWindowDays = days <= 14 ? 3 : 7;
  const groupSmoothingWindowDays = days <= 30 ? orgSmoothingWindowDays : 14;
  const comparisonWindowDays = days <= 7 ? 7 : days <= 30 ? 14 : days <= 60 ? 28 : 45;
  const maxSmoothingWindowDays = Math.max(orgSmoothingWindowDays, groupSmoothingWindowDays);

  return {
    orgSmoothingWindowDays,
    comparisonWindowDays,
    groupSmoothingWindowDays,
    queryDays: Math.min(
      DEVEX_REVIEW_LATENCY_MAX_DAYS,
      Math.max(comparisonWindowDays * 2, days + maxSmoothingWindowDays - 1)
    ),
  };
}
